import { pick } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import store2 from 'store2';
import { ObjectStates as ObjectStatus } from '../../../../common/src/enums/ObjectStates';

import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchDelete, fetchGet } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { SortColumn, SortDirections, updateSortColumns } from '../../utils/sorting';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterMultiSelect,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelSection,
    ISelectOption,
    ITopBarDropdownItem,
    PanelBody,
    PanelRouterLink,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortableHeader,
    Topbar,
    TopbarCrumbActive,
    TopBarDropdownButton,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { IAuthorization } from '../interfaces/IAuthorization';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { transformLabelsToSelectOptions } from '../utils/entityLabels.utils';
import { ListPager } from '../utils/ListPager';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import RoleDependent from '../utils/RoleDependent';
import { TagLabels } from '../utils/TagLabels';
import { ObjectTypeFieldSelect } from './ObjectTypeFieldSelect';

interface IObjectsListProps extends ITranslateProps, IRouterProps {
    dispatch: (prop: any) => void;
    location: any;
    useBatches: boolean;
    userRole: Roles;
    isClient: boolean;
    languageCode: string;
    useProductTitle: boolean;
    areLabelsVisibleInOverviews: boolean;
}

interface IObject {
    id: string;
    name: string;
    location: string;
    surveyId?: string;
    surveryedOn?: Date;
    surveyCondition?: string;
    surveyInspectionStandardType?: string;
    isPublic?: boolean;
    authorization: IAuthorization;
}

interface IValueText {
    value: string;
    text: string;
}

interface ISearchParameters {
    page: number;
    sortColumns: SortColumn[];
    objectName?: undefined | string;
    objectCode?: undefined | string;
    companyId?: undefined | string;
    surveyorId?: undefined | string;
    batchId?: undefined | string;
    status?: undefined | string;
    location?: undefined | string;
    locationCode?: undefined | string;
    objectTypeId?: undefined | string;
    attributes: { [key: string]: string };
    labels: string[];
    objectTypeField?: undefined | { id: string; value: string | number | boolean | undefined };
}
interface IObjectsListState {
    isLoading: boolean;
    items: IObject[];
    total: number;
    ownerCompanies: IValueText[];
    surveyorCompanies: IValueText[];
    batches: IValueText[];
    objectTypes: IValueText[];
    objectComplexLocations: IValueText[];
    knownLabels: ISelectOption[];
    searchParameters: ISearchParameters;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        useBatches: authentication.useBatches,
        userRole: authentication.role,
        isClient: authentication.isClient,
        languageCode: language.code,
        useProductTitle: authentication.useProductTitle,
        areLabelsVisibleInOverviews: authentication.areLabelsVisibleInOverviews,
    };
}

const storeKey = 'ObjectList';
const itemsPerPage = 50;

class ObjectsList extends Component<IObjectsListProps, IObjectsListState> {
    constructor(props: IObjectsListProps) {
        super(props);

        this.state = {
            isLoading: false,
            batches: [],
            ownerCompanies: [],
            surveyorCompanies: [],
            objectTypes: [],
            objectComplexLocations: [],
            knownLabels: [],
            searchParameters: {
                labels: [],
                sortColumns: [{ columnName: 'mostRecentSurvey.surveryedOn', direction: SortDirections.Descending }],
                page: 1,
                attributes: {},
            },
            items: [],
            total: 0,
        };
    }

    public override async componentDidMount() {
        if (this.props.location.query.complexCode || this.props.location.query.labels || this.props.location.query.ownerId) {
            const newState: any = this.state.searchParameters;
            const labelsFromQuery = this.props.location.query.labels;
            newState.companyId = this.props.location.query.ownerId;
            newState.objectCode = this.props.location.query.complexCode;
            newState.labels = [labelsFromQuery];
            await this.setState({ searchParameters: newState });
        } else if (store2.get(storeKey)) {
            await this.setState({ searchParameters: store2.get(storeKey) });
        }

        void this.search(this.props.location.query.page);
        void this.loadOwnerCompanies();
        void this.loadKnownLabels();

        if (this.props.useBatches) {
            void this.loadBatches();
        }
        void this.loadObjectTypes();
        if (isCustomerVersion('rijksvastgoed')) {
            await this.loadObjectComplexLocations();
        }
        if (isCustomerVersion('hyperplane')) {
            await this.loadSurveyorCompanies();
        }
    }

    public override async componentDidUpdate(prevProps: IObjectsListProps) {
        if (this.props.languageCode !== prevProps.languageCode) {
            await this.loadObjectTypes();
        }
    }

    public override render() {
        const isAdminOrClientSurveyorOperator =
            (this.props.isClient && (this.props.userRole === Roles.Operator || this.props.userRole === Roles.Surveyor)) ||
            this.props.userRole === Roles.Administrator;
        const isAdminOrClient = this.props.isClient || this.props.userRole === Roles.Administrator;

        const searchQuery = pick(this.state.searchParameters, [
            'objectName',
            'objectCode',
            'companyId',
            'batchId',
            'status',
            'location',
            'locationCode',
            'objectTypeId',
            'labels',
        ]);

        const exportItems: ITopBarDropdownItem[] = [
            {
                path: '/api/objects/download/excel',
                query: { languageCode: this.props.languageCode, ...searchQuery },
                text: this.props.t('common.objects'),
                icon: 'fa-file-excel-o',
            },
            {
                path: '/api/objects/download/shapeFiles',
                query: searchQuery,
                text: this.props.t('object.exportShapes'),
                icon: 'fa-globe',
            },
        ];

        if (isAdminOrClient && isCustomerVersion(['zxy', 'rijksvastgoed', 'utrecht', 'rotterdam'])) {
            // MIOK export expects attributes as JSON string
            const miokQuery: Record<string, any> = { ...searchQuery };
            if (miokQuery.attributes) {
                miokQuery.attributes = JSON.stringify(miokQuery.attributes);
            }
            const miokItem: ITopBarDropdownItem = {
                path: '/api/export/miokExcelObject',
                query: miokQuery,
                text: this.props.t('object.mjopReport'),
                icon: 'fa-file-excel-o',
                disabled: this.state.total > 250,
            };
            if (this.state.total > 250) {
                miokItem.maxLimitMessage = '(max 250)';
            }
            exportItems.push(miokItem);
        }

        if (isAdminOrClient && isCustomerVersion(['zxy', 'rijksvastgoed', 'utrecht', 'rotterdam'])) {
            // NEN export expects attributes as JSON string
            const nenQuery: Record<string, any> = { ...searchQuery };
            if (nenQuery.attributes) {
                nenQuery.attributes = JSON.stringify(nenQuery.attributes);
            }
            const nenItem: ITopBarDropdownItem = {
                path: '/api/export/nenExcelObject',
                query: nenQuery,
                text: this.props.t('object.nenReport'),
                icon: 'fa-file-excel-o',
                disabled: this.state.total > 250,
            };
            if (this.state.total > 250) {
                nenItem.maxLimitMessage = '(max 250)';
            }
            exportItems.push(nenItem);
        }

        return (
            <div>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title={this.props.t('common.objects')} icon="fa-building" />
                    </TopbarLeft>
                    <TopbarRight>
                        <TopBarDropdownButton
                            text={this.props.t('common.export')}
                            icon="fa-download"
                            disabled={this.state.items.length === 0}
                            items={exportItems}
                        />
                        <RoleDependent
                            allowed={[Roles.Administrator, Roles.Client]}
                            content={
                                <PanelRouterLink
                                    to="/objects/add"
                                    text={this.props.t('common.addItem', this.props.t('common.object'))}
                                    icon="fa fa-plus"
                                    small={true}
                                />
                            }
                        />
                    </TopbarRight>
                </Topbar>

                <Content>
                    <Tray>
                        <PanelWithoutForm>
                            <PanelBody noPadding={true}>
                                <Table hover={true}>
                                    <TableHead>
                                        <TableHeadRow light={true}>
                                            <TableSortableHeader
                                                header={this.props.t('common.name')}
                                                columnName="name"
                                                sortColumns={this.state.searchParameters.sortColumns}
                                                onClick={this.handleSortChanged.bind(this)}
                                            />
                                            <TableSortableHeader
                                                header={this.props.t('common.location')}
                                                columnName="location"
                                                sortColumns={this.state.searchParameters.sortColumns}
                                                onClick={this.handleSortChanged.bind(this)}
                                            />
                                            <TableSortableHeader
                                                header={
                                                    this.props.useProductTitle
                                                        ? this.props.t('object.lastProductDate')
                                                        : this.props.t('object.lastSurveyDate')
                                                }
                                                columnName="mostRecentSurvey.surveryedOn"
                                                sortColumns={this.state.searchParameters.sortColumns}
                                                onClick={this.handleSortChanged.bind(this)}
                                            />

                                            <TableHeader>&nbsp;</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    {(this.state.isLoading && (
                                        <TableBody>
                                            <TableRow>
                                                <TableData colSpan={5}>
                                                    <Spinner />
                                                </TableData>
                                            </TableRow>
                                        </TableBody>
                                    )) || (
                                        <TableBody>
                                            {this.state.items.map((item: any) => (
                                                <TableRow key={item.id}>
                                                    <TableData onClick={() => this.rowClicked(`/objects/${item.id}`)}>
                                                        {item.name}
                                                        {this.props.areLabelsVisibleInOverviews ?? <TagLabels labels={item.labels} />}
                                                    </TableData>
                                                    <TableData onClick={() => this.rowClicked(`/objects/${item.id}`)}>{item.location}</TableData>
                                                    <TableData nowrap={true} onClick={() => this.rowClicked(`/objects/${item.id}`)}>
                                                        <NullableFormattedDate value={item.surveryedOn} />{' '}
                                                    </TableData>

                                                    <td style={{ whiteSpace: 'nowrap', width: '80px' }}>
                                                        {item.authorization.canEdit && (
                                                            <IconEditButton onClick={() => this.props.navigate(`/objects/${item.id}/edit`)} />
                                                        )}
                                                        {item.authorization.canDelete && (
                                                            <IconRemoveButton
                                                                removeQuestion={this.props.t('common.confirmDelete', item.name)}
                                                                onClick={async () => this.remove(item.id)}
                                                            />
                                                        )}
                                                    </td>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    )}
                                </Table>
                            </PanelBody>
                            <ListPager
                                disabled={this.state.isLoading}
                                itemsPerPage={itemsPerPage}
                                total={this.state.total}
                                page={this.state.searchParameters.page}
                                handleNavigation={this.handleNavigation.bind(this)}
                            />
                        </PanelWithoutForm>
                    </Tray>
                    {/* <ObjectsListFilter disabled={this.state.isLoading} onSearch={this.onFilter.bind(this)} initalFilter={this.state.searchParameters.filter} /> */}

                    <RightTray>
                        <form
                            id="filter"
                            onSubmit={(e) => {
                                e.preventDefault();
                                void this.search();
                            }}
                        >
                            <div className="admin-form">
                                <h4> {this.props.t('object.filter')}</h4>
                                <EditPanelSection topBorder={true}>
                                    <EditPanelInput
                                        name="objectName"
                                        placeholder={this.props.t('object.nameOrCode')}
                                        value={this.state.searchParameters.objectName}
                                        icon="fa fa-building"
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={this.search.bind(this)}
                                    />
                                </EditPanelSection>
                                <EditPanelSection>
                                    {(isCustomerVersion('rijksvastgoed') && (
                                        <EditPanelFilterSelect
                                            name="locationCode"
                                            value={this.state.searchParameters.locationCode}
                                            handleChange={this.handleChange.bind(this)}
                                            placeholder={this.props.t('common.selectItem', this.props.t('common.location'))}
                                            options={this.state.objectComplexLocations}
                                        />
                                    )) || (
                                        <EditPanelInput
                                            name="location"
                                            placeholder={this.props.t('common.location')}
                                            value={this.state.searchParameters.location}
                                            icon="fa fa-map-marker"
                                            handleChange={this.handleChange.bind(this)}
                                            handleEnter={this.search.bind(this)}
                                        />
                                    )}
                                </EditPanelSection>
                                <EditPanelSection>
                                    <EditPanelFilterSelect
                                        name="objectTypeId"
                                        value={this.state.searchParameters.objectTypeId}
                                        handleChange={this.handleChange.bind(this)}
                                        placeholder={this.props.t('common.selectItem', this.props.t('common.objectType'))}
                                        options={this.state.objectTypes}
                                    />
                                </EditPanelSection>
                                {this.state.searchParameters.objectTypeId && (
                                    <ObjectTypeFieldSelect
                                        objectTypeId={this.state.searchParameters.objectTypeId}
                                        onChange={this.handleChange.bind(this)}
                                        name="objectTypeField"
                                    />
                                )}
                                {isAdminOrClientSurveyorOperator && (
                                    <>
                                        <EditPanelSection>
                                            <EditPanelFilterSelect
                                                name="companyId"
                                                value={this.state.searchParameters.companyId}
                                                handleChange={this.handleChange.bind(this)}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.owner'))}
                                                options={this.state.ownerCompanies}
                                            />
                                        </EditPanelSection>
                                    </>
                                )}
                                {isCustomerVersion('hyperplane') && isAdminOrClient && (
                                    <EditPanelSection>
                                        <EditPanelFilterSelect
                                            name="surveyorId"
                                            value={this.state.searchParameters.surveyorId}
                                            handleChange={this.handleChange.bind(this)}
                                            disabled={this.state.isLoading}
                                            placeholder={this.props.t('common.selectItem', this.props.t('common.surveyor'))}
                                            options={this.state.surveyorCompanies}
                                        />
                                    </EditPanelSection>
                                )}
                                <EditPanelSection>
                                    <EditPanelFilterSelect
                                        name="status"
                                        value={this.state.searchParameters.status}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        placeholder={this.props.t('common.selectItem', this.props.t('common.status'))}
                                        options={this.objectStates()}
                                    />
                                </EditPanelSection>

                                {this.state.knownLabels.length > 0 && (
                                    <EditPanelSection>
                                        <EditPanelFilterMultiSelect
                                            name="labels"
                                            placeholder={this.props.t('common.selectItem', this.props.t('common.labels'))}
                                            value={this.state.searchParameters.labels}
                                            options={this.state.knownLabels}
                                            handleChange={this.handleChange.bind(this)}
                                        />
                                    </EditPanelSection>
                                )}

                                {this.props.useBatches && (
                                    <EditPanelSection>
                                        <EditPanelFilterSelect
                                            name="batchId"
                                            value={this.state.searchParameters.batchId}
                                            handleChange={this.handleChange.bind(this)}
                                            disabled={this.state.isLoading}
                                            placeholder={this.props.t('common.selectItem', 'Batch')}
                                            options={this.state.batches}
                                        />
                                    </EditPanelSection>
                                )}
                                <EditPanelSection>
                                    <EditPanelInput
                                        name="objectCode"
                                        placeholder={this.props.t('object.codeObjectComplex')}
                                        value={this.state.searchParameters.objectCode}
                                        icon="fa fa-circle-o"
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={this.search.bind(this)}
                                    />
                                </EditPanelSection>
                                <EditPanelButtons topBorder>
                                    <EditPanelButton
                                        onClick={async () => this.resetForm()}
                                        text={this.props.t('common.reset')}
                                        disabled={this.state.isLoading}
                                    />
                                    <EditPanelButton
                                        onClick={async () => this.search()}
                                        text={this.props.t('common.search')}
                                        primary
                                        disabled={this.state.isLoading}
                                    />
                                </EditPanelButtons>
                            </div>
                        </form>
                    </RightTray>
                </Content>
            </div>
        );
    }
    private async handleSortChanged(columnName: string, direction: SortDirections | undefined) {
        const sortColumns = updateSortColumns(columnName, direction, this.state.searchParameters.sortColumns);
        await this.setState({ searchParameters: { ...this.state.searchParameters, sortColumns } });

        await this.search(this.props.location.query.page);
    }

    private async remove(id: string) {
        const url = `/api/objects/${id}`;
        await fetchDelete(url);
        await this.search();
    }

    private handleNavigation(page: number) {
        this.props.navigate({ pathname: '/objects', search: `?page=${page}` });
        void this.search(page.toString());
    }

    private rowClicked(url: string) {
        this.props.navigate(url);
    }

    private async handleChange(value: any, stateKey: string) {
        const newState: ISearchParameters = { ...this.state.searchParameters, ...{ [stateKey]: value } };
        if (stateKey === 'objectTypeId') {
            newState.objectTypeField = undefined;
        }
        await this.setState({ searchParameters: newState });
        let shouldSearch = true;

        if (stateKey === 'objectName' || stateKey === 'location' || stateKey === 'objectCode') {
            shouldSearch = false;
        }
        if (stateKey === 'objectTypeField') {
            shouldSearch = value?.shouldSubmit ?? false;
        }

        if (shouldSearch) {
            await this.search();
        }
    }

    private objectStates() {
        const options = [
            { value: ObjectStatus.UnderConstruction, text: this.props.t('objectStatus.underConstruction') },
            { value: ObjectStatus.InUse, text: this.props.t('objectStatus.inUse') },
            { value: ObjectStatus.InMaintenance, text: this.props.t('objectStatus.inMaintenance') },
            { value: ObjectStatus.OutOfService, text: this.props.t('objectStatus.outOfService') },
            { value: ObjectStatus.Cancelled, text: this.props.t('objectStatus.cancelled') },
        ];
        if (this.props.userRole === Roles.Administrator || this.props.isClient) {
            options.push({ value: ObjectStatus.Deleted, text: this.props.t('objectStatus.deleted') });
        }
        return options;
    }

    private async loadOwnerCompanies() {
        const result = await fetchGet('/api/companyList?type=owner', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ ownerCompanies: valueTextArray });
        }
    }

    private async loadSurveyorCompanies() {
        const result = await fetchGet('/api/companyList?type=surveyor', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ surveyorCompanies: valueTextArray });
        }
    }

    private async loadObjectComplexLocations() {
        const result = await fetchGet('/api/objectsComplexList', {});
        await this.setState({ objectComplexLocations: result.json?.items });
    }

    private async loadBatches() {
        const result = await fetchGet('/api/batcheslist', {}, [], [403]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({
                value: item.id,
                text: `${item.name.substring(0, 42)}${item.name.length > 43 ? '...' : ''}`,
            }));
            await this.setState({ batches: valueTextArray });
        }
    }

    private async loadObjectTypes() {
        const result = await fetchGet('/api/objectTypesAvailable', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ objectTypes: valueTextArray });
        }
    }

    private async resetForm() {
        await this.setState({
            searchParameters: {
                ...this.state.searchParameters,
                attributes: { palen: '' },
                batchId: undefined,
                companyId: undefined,
                labels: [],
                location: undefined,
                objectCode: undefined,
                objectName: undefined,
                objectTypeId: undefined,
                status: undefined,
                objectTypeField: undefined,
                locationCode: undefined,
                page: 0,
            },
        });
        await this.search();
    }

    private async search(pageString?: string) {
        const pageNumber = pageString ? parseInt(pageString, 10) : 1;
        await this.setState({
            isLoading: true,
            searchParameters: { ...this.state.searchParameters, page: pageNumber },
        });

        store2.set(storeKey, this.state.searchParameters);

        const queryString: { [key: string]: undefined | string | number | object } = {
            ...pick(this.state.searchParameters, [
                'objectName',
                'objectCode',
                'companyId',
                'surveyorId',
                'batchId',
                'status',
                'location',
                'locationCode',
                'objectTypeId',
                'labels',
                'sortColumns',
                'description',
            ]),
            limit: itemsPerPage,
            offset: itemsPerPage * (pageNumber - 1),
        };

        if (this.state.searchParameters.attributes && this.state.searchParameters.attributes.palen) {
            queryString['attributes.palen'] = this.state.searchParameters.attributes.palen;
        }

        if (this.state.searchParameters.objectTypeField?.value !== undefined) {
            queryString['objectTypeField'] = this.state.searchParameters.objectTypeField;
        }

        const result = await fetchGet('/api/objects', queryString);
        await this.setState({
            isLoading: false,
            items: result.json.items,
            total: result.json.total,
        });
    }

    private async loadKnownLabels() {
        const url = '/api/objects/knownLabels';
        const result = await fetchGet(url);
        const knownLabels = transformLabelsToSelectOptions(result?.json);
        await this.setState({ knownLabels });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(ObjectsList as any) as any);
